import {
  Button,
  ConfigProvider,
  ConfirmModal,
  Empty,
  Flex,
  HintIcon,
  Loading,
  Table,
  Text,
  ToggleAllButton,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { PinpointMarkerForTableCell } from '@/features/comment/components/pinpoint/PinpointMarkerForTableCell';
import { pinpointTableActionColumn } from '@/features/comment/components/pinpoint/PinpointTableRowMenuCell';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import {
  getPinpointSceneAndFieldBySheetType,
  getPinpointSheetType,
} from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import { paymentNumberConverter } from '@/features/project/_utils/payment';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { PaymentModel } from '@/features/project/projects/_models/payment';
import type {
  PaymentGroupModel,
  PaymentGroupsModel,
} from '@/features/project/projects/_models/payment-group';
import { useDeletePayment } from '@/features/project/projects/detail/payment/_hooks/payment';
import {
  useDeletePaymentGroup,
  usePaymentGroups,
} from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import {
  AmountUnitLabelModifier,
  TABLE_MONETARY_AMOUNT_WIDTH,
  formatValueByUnit,
} from '@/utils';
import classNames from 'classnames';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { PaymentFormDrawer } from './PaymentFormDrawer';
import { PaymentGroupFormDrawer } from './PaymentGroupDrawer';
import styles from './PaymentGroupsTable.module.scss';
import { PaymentsTable } from './PaymentsTable';

const tableStyles = classNames(
  styles.ant_table_content,
  styles.ant_table_thead,
);

const PREVIEW_ROW: z.infer<typeof PaymentGroupModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  displayCode: 'A',
  name: '',
  paymentCount: null,
  overview: null,
  projectId: '',
  payments: [],
  totalAmount: null,
};

export const PaymentGroupsTable: FC = () => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const commentStackSiderView = useCommentStackSiderView();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const previewTableRef = useRef<HTMLDivElement>(null);

  const [paymentGroupForDelete, setPaymentGroupForDelete] = useState<z.infer<
    typeof PaymentGroupModel
  > | null>(null);
  const [paymentForDelete, setPaymentForDelete] = useState<z.infer<
    typeof PaymentModel
  > | null>(null);
  const paymentGroups = usePaymentGroups({ projectId });
  const project = useProject(projectId);
  const { mutateAsync: deletePaymentGroupAsync } = useDeletePaymentGroup();
  const { mutateAsync: deletePaymentAsync } = useDeletePayment();
  const editPermission = useProjectPermission(true);
  const { devtoolState } = useDevToolsStorage();
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    onExpand,
    removeExpandedRowKeys,
  } = useExpandedRowKeysContext();
  const [allPaymentKeys, setAllPaymentKeys] = useState<string[]>([]);

  const showPreviewRow = useMemo(
    () => previewRowState?.type === 'payment-group',
    [previewRowState],
  );

  const tableData = showPreviewRow
    ? [...(paymentGroups.data ?? []), PREVIEW_ROW]
    : paymentGroups.data;

  useEffect(() => {
    if (showPreviewRow) {
      previewTableRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [showPreviewRow]);

  const isKsStandardAfter2025 = useMemo(
    () =>
      project.data?.sheetType === 'KS' &&
      project.data.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project.data),
    [project],
  );

  const initialAllPaymentKeys = useMemo(() => {
    const keys = paymentGroups.data?.reduce<string[]>((acc, cur) => {
      acc.push(cur.id);
      const paymentsIds = cur.payments.map((payment) => payment.id);
      return acc.concat(paymentsIds);
    }, []);
    return keys ?? [];
  }, [paymentGroups.data]);

  useEffect(() => {
    if (initialAllPaymentKeys.length) {
      setAllPaymentKeys(initialAllPaymentKeys);
    }
  }, [initialAllPaymentKeys]);

  if (paymentGroups.isPending || !paymentGroups.data) {
    return <Loading />;
  }

  const openAllExpanded = () => {
    addExpandedRowKeys(allPaymentKeys);
  };

  const closeAllExpanded = () => {
    removeExpandedRowKeys(allPaymentKeys);
  };

  const pinpointSheetType = getPinpointSheetType(
    project.data?.sheetType ?? 'RS',
    project.data?.fundAdditionalAttributes?.fundSheetType,
  );

  const renderTable = (
    data: z.infer<typeof PaymentGroupsModel>,
    idx: number,
  ) => {
    return (
      <Table
        enableSticky={false}
        key={`payment-group-table-${idx}`}
        rowKey="id"
        dataSource={data}
        className={tableStyles}
        style={{ minWidth: 1080 }}
        showHeader={idx === 0}
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          columnWidth: 0,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            key: 'displayCode',
            dataIndex: 'displayCode',
            align: 'right',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return <Text text={value} />;
            },
            width: 38,
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('payment-group-name')}
              />
            ),
            key: 'name',
            dataIndex: 'name',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: t('payment-group'),
                    })}
                  />
                );
              }
              return (
                <PinpointMarkerForTableCell
                  filteredOrganizationFiscalYear={project.data?.fiscalYear}
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType,
                    rs: {
                      path: { project: projectId, paymentGroup: record.id },
                      field: 'rs.payment-group',
                    },
                    ss: {
                      path: { project: projectId, paymentGroup: record.id },
                      field: 'ss.payment-group-row',
                    },
                    ksStandard: {
                      path: { project: projectId, paymentGroup: record.id },
                      field: 'ks.standard.payment-group-row',
                    },
                  })}
                >
                  <Text text={value} />
                </PinpointMarkerForTableCell>
              );
            },
            width: 380,
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('total-expenditure')}
              />
            ),
            key: 'totalAmount',
            dataIndex: 'totalAmount',
            align: 'right',
            width: TABLE_MONETARY_AMOUNT_WIDTH,
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return (
                <Text
                  text={formatValueByUnit(value, {
                    operation: 'round',
                    unit,
                    negativeCount: record.negativeTotalAmountCount,
                    isDebugMode: devtoolState?.isDebugMode,
                  })}
                />
              );
            },
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('payment-count')}
              />
            ),
            key: 'paymentCount',
            dataIndex: 'paymentCount',
            align: 'right',
            width: 120,
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return <Text text={paymentNumberConverter(value)} />;
            },
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={t('payment-group-overview')}
              />
            ),
            key: 'overview',
            dataIndex: 'overview',
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return <Text text={value} maxRows={2} />;
            },
          },
          pinpointTableActionColumn<z.infer<typeof PaymentGroupModel>>({
            showWithoutPinpoint: !!(
              project.data &&
              !project.data.isLocked &&
              editPermission.data
            ),
            render: (record) => ({
              pinpoint: {
                project: project.data,
                onPinpoint: () => {
                  commentStackSiderView.set({
                    payload: [
                      {
                        config: {
                          viewType: 'comment-form',
                          target: {
                            projectFiscalYear: project.data?.fiscalYear,
                          },
                          isEditing: false,
                          pinpoint: getPinpointSceneAndFieldBySheetType({
                            pinpointSheetType,
                            rs: {
                              path: {
                                project: projectId,
                                paymentGroup: record.id,
                              },
                              field: 'rs.payment-group',
                            },
                            ss: {
                              path: {
                                project: projectId,
                                paymentGroup: record.id,
                              },
                              field: 'ss.payment-group-row',
                            },
                            ksStandard: {
                              path: {
                                project: projectId,
                                paymentGroup: record.id,
                              },
                              field: 'ks.standard.payment-group-row',
                            },
                          }),
                        },
                        siderType: 'comment',
                      },
                    ],
                    target: {
                      type: 'project',
                      projectId,
                    },
                  });
                },
              },
              onEdit: () => {
                setDrawerState({
                  open: true,
                  type: 'payment-group',
                  isEditing: true,
                  paymentGroupId: record.id,
                });
              },
              items: [
                {
                  key: 'delete',
                  label: t('delete'),
                  danger: true,
                  onClick: () => {
                    setPaymentGroupForDelete(record);
                  },
                },
              ],
            }),
          }),
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (paymentGroup) => (
            <PaymentsTable
              data={paymentGroup.payments}
              paymentGroupId={paymentGroup.id}
              onClickDelete={(value) => {
                setPaymentForDelete(value);
              }}
            />
          ),
        }}
        pagination={false}
      />
    );
  };

  return (
    <Flex vertical gap="small">
      <Flex justify="space-between">
        <ToggleAllButton
          title={
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: pinpointSheetType,
                rs: {
                  field: 'rs.payment-title',
                  path: { project: projectId },
                },
                ss: {
                  field: 'ss.payment-title',
                  path: { project: projectId },
                },
                ksStandard: {
                  field: 'ks.standard.payment-title',
                  path: { project: projectId },
                },
              })}
            >
              <Flex align="center" gap={4}>
                <Text
                  variant="h3"
                  text={
                    isKsStandardAfter2025
                      ? t(
                          'payment-list-with-unit-expenditure-to-each-block-in-previous-year',
                          {
                            unit: AmountUnitLabelModifier.thousand,
                          },
                        )
                      : t('payment-list-with-unit', {
                          unit: AmountUnitLabelModifier.thousand,
                        })
                  }
                />
                <HintIcon hint={t('hint-payment-list-info')} />
              </Flex>
            </PinpointMarker>
          }
          openAll={openAllExpanded}
          closeAll={closeAllExpanded}
        />
        {project.data && !project.data.isLocked && editPermission.data && (
          <PublishedProjectMutableGuard>
            <Button
              type="primary"
              onClick={() => {
                setDrawerState({
                  open: true,
                  type: 'payment-group',
                  isEditing: false,
                });
                setPreviewRowState({
                  type: 'payment-group',
                });
                setSelectedRowKeys([PREVIEW_ROW_ID]);
              }}
            >
              {t('create-payment-group')}
            </Button>
          </PublishedProjectMutableGuard>
        )}
      </Flex>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: 'white',
              headerColor: '#00000073',
              rowExpandedBg: 'white',
              headerSplitColor: '#C0C0C0',
            },
          },
        }}
      >
        <Flex vertical gap={24}>
          {tableData?.length ? (
            tableData.map(({ id }, idx) => (
              <div key={id} ref={showPreviewRow ? previewTableRef : undefined}>
                {renderTable(
                  tableData.filter((_, i) => i === idx),
                  idx,
                )}
              </div>
            ))
          ) : (
            <Empty />
          )}
        </Flex>

        <PaymentGroupFormDrawer />

        <ConfirmModal
          open={paymentGroupForDelete !== null}
          title={t('delete-confirmation')}
          disclaimerText={t('delete-item-confirmation', {
            item: paymentGroupForDelete?.name ?? '',
          })}
          onOk={async () => {
            invariant(paymentGroupForDelete, t('unreachable-code'));
            try {
              await deletePaymentGroupAsync({
                id: paymentGroupForDelete.id,
                projectId: paymentGroupForDelete.projectId,
              });
              await Promise.all([
                openMessage('success', t('message-delete-completed')),
                (async () => {
                  setPaymentGroupForDelete(null);
                })(),
              ]);
            } catch {
              await openMessage('error', t('message-delete-failed'));
            }
          }}
          onCancel={() => {
            setPaymentGroupForDelete(null);
          }}
          skipCheck
        />

        <PaymentFormDrawer type="payee" />

        <ConfirmModal
          open={paymentForDelete !== null}
          title={t('delete-confirmation')}
          disclaimerText={t('delete-item-confirmation', {
            item: paymentForDelete?.name ?? '',
          })}
          onOk={async () => {
            invariant(paymentForDelete, t('unreachable-code'));
            try {
              await deletePaymentAsync({
                id: paymentForDelete.id,
                projectId,
                paymentGroupId: paymentForDelete.paymentGroupId,
              });
              await Promise.all([
                openMessage('success', t('message-delete-completed')),
                (async () => {
                  setPaymentForDelete(null);
                })(),
              ]);
            } catch {
              await openMessage('error', t('message-delete-failed'));
            }
          }}
          onCancel={() => {
            setPaymentForDelete(null);
          }}
          skipCheck
        />
      </ConfigProvider>
    </Flex>
  );
};
